import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { VoiceButtonProps } from '../types';
import { useChatContext } from '../context/ChatContext';
import { useAnnouncer } from '../context/AccessibilityContext';
import { KEYBOARD_KEYS } from '../utils/accessibility';
import './VoiceButton.css';

export const VoiceButton: React.FC<VoiceButtonProps> = ({
  onVoiceData,
  onVoiceStart,
  onVoiceStop,
  isRecording,
  disabled = false
}) => {
  const { dispatch } = useChatContext();
  const announcer = useAnnouncer();
  const { t } = useTranslation();
  const [permissionStatus, setPermissionStatus] = useState<'granted' | 'denied' | 'prompt' | 'checking'>('prompt');
  const [error, setError] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  // Check microphone permissions on mount
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const permission = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        setPermissionStatus(permission.state as 'granted' | 'denied' | 'prompt');
        
        // Listen for permission changes
        permission.onchange = () => {
          setPermissionStatus(permission.state as 'granted' | 'denied' | 'prompt');
        };
      } catch (err) {
        console.warn('Permission API not supported, will check on first use');
        setPermissionStatus('prompt');
      }
    };

    checkPermissions();
  }, []);

  // Start recording
  const startRecording = useCallback(async () => {
    try {
      setError(null);
      announcer.announce(t('voice.recordingStarted'), 'polite');
      
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        }
      });
      
      streamRef.current = stream;
      setPermissionStatus('granted');
      
      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      
      // Handle data available
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      
      // Handle recording stop
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(chunksRef.current, { 
          type: mediaRecorder.mimeType || 'audio/webm' 
        });
        onVoiceData(audioBlob);
        chunksRef.current = [];
        announcer.announce(t('voice.recordingStopped'), 'polite');
      };
      
      // Start recording
      mediaRecorder.start();
      dispatch({ type: 'START_RECORDING' });
      announcer.announce(t('voice.recordingInProgress'), 'assertive');
      
      // Notify parent about voice session start
      if (onVoiceStart) {
        onVoiceStart();
      }
      
    } catch (err) {
      console.error('Failed to start recording:', err);
      
      let errorMessage = t('voice.recordingError');
      
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          errorMessage = t('voice.microphonePermissionDenied');
          setPermissionStatus('denied');
        } else if (err.name === 'NotFoundError') {
          errorMessage = t('voice.recordingError');
        } else if (err.name === 'NotSupportedError') {
          errorMessage = t('voice.voiceNotSupported');
        } else {
          errorMessage = t('voice.recordingError');
        }
      }
      
      setError(errorMessage);
      announcer.announce(errorMessage, 'assertive');
      dispatch({ type: 'STOP_RECORDING' });
      
      // Notify parent about voice session stop on error
      if (onVoiceStop) {
        onVoiceStop();
      }
    }
  }, [dispatch, onVoiceData, announcer, t]);

  // Stop recording
  const stopRecording = useCallback(() => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      
      dispatch({ type: 'STOP_RECORDING' });
      setError(null);
      
      // Notify parent about voice session stop
      if (onVoiceStop) {
        onVoiceStop();
      }
    } catch (err) {
      console.error('Failed to stop recording:', err);
      setError(t('voice.recordingError'));
      dispatch({ type: 'STOP_RECORDING' });
    }
  }, [dispatch, t]);

  // Handle button click - toggle behavior like Grok
  const handleClick = useCallback(async () => {
    if (disabled) return;
    
    if (isRecording) {
      // Stop recording and close WebSocket connection
      stopRecording();
      setIsReady(false);
      if (onVoiceStop) {
        onVoiceStop();
      }
    } else {
      // Start recording and open WebSocket connection
      setIsReady(true);
      if (onVoiceStart) {
        onVoiceStart();
      }
      await startRecording();
    }
  }, [disabled, isRecording, startRecording, stopRecording, onVoiceStart, onVoiceStop]);

  // Handle keyboard events
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    
    // Space or Enter to toggle recording
    if (e.key === KEYBOARD_KEYS.SPACE || e.key === KEYBOARD_KEYS.ENTER) {
      e.preventDefault();
      handleClick();
    }
    
    // Escape to stop recording
    if (e.key === KEYBOARD_KEYS.ESCAPE && isRecording) {
      e.preventDefault();
      stopRecording();
      announcer.announce(t('voice.recordingCancelled'), 'polite');
    }
  }, [disabled, handleClick, isRecording, stopRecording, announcer, t]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      if (streamRef.current && streamRef.current.getTracks) {
        const tracks = streamRef.current.getTracks();
        if (tracks) {
          tracks.forEach(track => track.stop());
        }
      }
    };
  }, []);

  // Get button state classes
  const getButtonClasses = () => {
    const baseClass = 'voice-button';
    const classes = [baseClass];
    
    if (isRecording) classes.push(`${baseClass}--recording`);
    if (isReady && !isRecording) classes.push(`${baseClass}--ready`);
    if (disabled) classes.push(`${baseClass}--disabled`);
    if (permissionStatus === 'denied') classes.push(`${baseClass}--denied`);
    if (error) classes.push(`${baseClass}--error`);
    
    return classes.join(' ');
  };

  // Get aria label based on state
  const getAriaLabel = () => {
    if (disabled) return t('voice.voiceNotSupported');
    if (permissionStatus === 'denied') return t('voice.microphonePermissionDenied');
    if (isRecording) return t('voice.stopRecording');
    return t('voice.startRecording');
  };

  // Get button title/tooltip
  const getTitle = () => {
    if (error) return error;
    if (permissionStatus === 'denied') return t('voice.microphonePermissionDenied');
    if (permissionStatus === 'checking') return t('common.loading');
    if (isRecording) return t('voice.recordingInProgress');
    return t('voice.startRecording');
  };

  return (
    <div className="voice-button-container">
      <button
        type="button"
        className={`${getButtonClasses()} accessible-button`}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-label={getAriaLabel()}
        aria-describedby={error ? 'voice-button-error' : undefined}
        aria-pressed={isRecording}
        title={getTitle()}
      >
        {/* Microphone icon */}
        <svg
          className="voice-button__icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {permissionStatus === 'denied' ? (
            // Microphone off icon
            <>
              <line x1="1" y1="1" x2="23" y2="23"></line>
              <path d="m9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path>
              <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"></path>
              <line x1="12" y1="19" x2="12" y2="23"></line>
              <line x1="8" y1="23" x2="16" y2="23"></line>
            </>
          ) : isRecording ? (
            // Live listening icon (microphone with sound waves)
            <>
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
              <line x1="12" y1="19" x2="12" y2="23"></line>
              <line x1="8" y1="23" x2="16" y2="23"></line>
              {/* Sound waves */}
              <path d="M20 8c0-1.5-.5-3-1.5-4" opacity="0.7"></path>
              <path d="M21 6c0-2-1-4-2.5-5.5" opacity="0.5"></path>
              <path d="M4 8c0-1.5.5-3 1.5-4" opacity="0.7"></path>
              <path d="M3 6c0-2 1-4 2.5-5.5" opacity="0.5"></path>
            </>
          ) : (
            // Regular microphone icon
            <>
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
              <line x1="12" y1="19" x2="12" y2="23"></line>
              <line x1="8" y1="23" x2="16" y2="23"></line>
            </>
          )}
        </svg>
        
        {/* Recording indicator with wave animation */}
        {isRecording && (
          <div className="voice-button__recording-indicator" aria-hidden="true">
            <div className="voice-button__pulse"></div>
          </div>
        )}
        
        {/* Ready indicator with wave animation */}
        {isReady && !isRecording && (
          <div className="voice-button__wave-indicator" aria-hidden="true">
            <div className="voice-button__wave"></div>
            <div className="voice-button__wave"></div>
            <div className="voice-button__wave"></div>
          </div>
        )}
      </button>
      
      {/* Live listening indicator */}
      {isRecording && !error && (
        <div className="voice-button__status" aria-hidden="true">
          {t('voice.recordingInProgress')}
        </div>
      )}
      
      {/* Error message */}
      {error && (
        <div 
          id="voice-button-error" 
          className="voice-button__error error-message" 
          role="alert" 
          aria-live="assertive"
        >
          {error}
        </div>
      )}
    </div>
  );
};